/// <reference types="vite/client" />
import {
  HeadContent,
  Scripts,
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import * as React from 'react';
import { IntlayerProvider } from 'react-intlayer';

import { createRootHeadConfig } from './constants';
import { beforeRootLoad } from './utils';

import { CookiePolicyPopup } from '~/components/CookiePolicyPopup';
import { Footer } from '~/components/Footer';
import { Header } from '~/components/Header';
import { ThemeProvider } from '~/components/theme';
import { AnalyticsProvider } from '~/lib/analytics/AnalyticsProvider';
import { getAnalyticsConfig, type PublicAppConfig } from '~/lib/config/env';
import {
  getOpenVacanciesCount,
  type PeopleForceVacanciesCountPayload,
} from '~/lib/peopleforce';
import type { RouterContext } from '~/router';
import appCss from '~/styles/app.css?url';
import { DEFAULT_LOCALE, isAppLocale, type AppLocale } from '~/utils/i18n';
import '~/dictionaries';

const HEALTH_ROUTE_PATHNAME = '/health';
const HEALTH_ROUTE_OPEN_VACANCIES_COUNT: PeopleForceVacanciesCountPayload = {
  status: 'unconfigured',
  count: null,
};

const RootDocument = () => {
  // useRouterState with selector subscribes to location changes (reactive)
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const openVacanciesCount = Route.useLoaderData();

  // Extract locale from URL (e.g., "/en/page" → "en")
  const urlLocale = pathname.split('/')[1];
  const locale = isAppLocale(urlLocale) ? urlLocale : DEFAULT_LOCALE;
  const publicAppConfig: PublicAppConfig = {
    analytics: getAnalyticsConfig(true),
  };
  const publicAppConfigJson = JSON.stringify(publicAppConfig).replace(
    /</g,
    '\\u003c'
  );

  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        {/* Anti-flash script: apply theme class before paint to prevent FOUC */}
        <script
          dangerouslySetInnerHTML={{
            __html: `document.documentElement.classList.add('dark');`,
          }}
        />
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.__APP_CONFIG__ = ${publicAppConfigJson};
            `,
          }}
        />
      </head>
      <body suppressHydrationWarning>
        <ThemeProvider
          attribute="class"
          defaultTheme="dark"
          forcedTheme="dark"
          disableTransitionOnChange
        >
          <IntlayerProvider
            key={locale}
            defaultLocale={DEFAULT_LOCALE}
            locale={locale}
          >
            <AnalyticsProvider>
              <div className="min-h-screen bg-black flex flex-col max-w-[1680px] mx-auto">
                <Header openPositionsCount={openVacanciesCount.count} />
                <Outlet />
                <Footer />
              </div>
              <CookiePolicyPopup />
            </AnalyticsProvider>
          </IntlayerProvider>
        </ThemeProvider>
        <Scripts />
      </body>
    </html>
  );
};

type AllRouteParams = {
  locale?: AppLocale;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: beforeRootLoad,
  loader: ({ location }) => {
    if (location.pathname === HEALTH_ROUTE_PATHNAME) {
      return HEALTH_ROUTE_OPEN_VACANCIES_COUNT;
    }

    return getOpenVacanciesCount();
  },
  head: (context) => {
    const params = context.params as AllRouteParams;
    const locale = params.locale ?? DEFAULT_LOCALE;

    return createRootHeadConfig({
      locale,
      appStylesHref: appCss,
    });
  },
  component: RootDocument,
});
